import { cn } from '@eliadmin/core/src/utils/style-utils';

/**
 * Heading — jediné miesto, kde sa definuje nadpis sekcie.
 *
 * Drží dvojicu eyebrow (podnadpis) + nadpis vrátane ich vzájomných medzier,
 * zarovnania a farieb na svetlom aj tmavom podklade. Sekcie nikdy nepíšu holé
 * `<h2>` ani vlastný eyebrow — keď dizajn vyžaduje niečo nové, pridáva sa to
 * SEM, nie na call site.
 *
 * Serverový komponent — nemá stav ani obsluhu udalostí, takže nemá dôvod ísť
 * do klientskeho balíka. Používa sa v každej sekcii, takže je to citeľné.
 *
 * Figma: eyebrow = komponent "Eyebrow" (8:8), nadpisy = Heading/H1–H6 (33:3).
 * Hranaté zátvorky okolo eyebrow sú súčasťou dizajnu, nie obsahu — dopĺňa ich
 * komponent, takže `subtitle` sa píše normálne ("O nás", nie "[ O NÁS ]").
 * Veľké písmená rieši trieda `.eyebrow` / `.h*` v headings.css.
 */
export interface HeadingProps {
  /**
   * Nadpis. Môže obsahovať HTML (napr. <br/> alebo <span> pre zvýraznenie).
   *
   * Voliteľný: sekcia Referencie má nad obsahom iba eyebrow a žiadny nadpis
   * (jeho miesto zaberá samotný citát). Bez `title` sa nadpisový element
   * nevykreslí vôbec — prázdny `<h2>` by kazil štruktúru dokumentu.
   */
  title?: string;
  /** Eyebrow nad nadpisom — bez zátvoriek, tie doplní komponent. */
  subtitle?: string;
  /**
   * Popis pod nadpisom (Figma Body/L, tlmená farba). Vo Figme je súčasťou
   * bloku „Section title", nie samostatný odstavec v sekcii — preto patrí sem.
   * Medzera nadpis → popis je 16, šírka je obmedzená na 688 ako vo Figme.
   */
  description?: string;
  /** Ktorý nadpisový element/škála sa použije. Hodnota je zároveň CSS trieda. */
  tag?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
  /** Semantic element independent from the visual type scale; defaults to tag. */
  as?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | 'p' | 'span';
  /** Vycentruje eyebrow aj nadpis (napr. sekcia Služby). */
  center?: boolean;
  /** Podklad, na ktorom nadpis leží — mení farby textu aj eyebrow. */
  theme?: 'light' | 'dark';
  /** Obmedzí šírku nadpisu, keď dizajn zalamuje na konkrétnu mieru. */
  titleMaxWidth?: string;
  className?: {
    container?: string;
    subtitle?: string;
    title?: string;
    description?: string;
  };
}

export function Heading({
  title,
  subtitle,
  description,
  tag = 'h2',
  as,
  center = false,
  theme = 'light',
  titleMaxWidth,
  className,
}: HeadingProps) {
  const Tag = as ?? tag;

  return (
    <div
      className={cn(
        'ea-heading__container',
        'flex w-full flex-col items-start',
        // Medzera eyebrow → nadpis: 16 na mobile, 32 na desktope.
        // Odmerané zo sekcie „Zaujímavé články": desktop `60041:3023` (eyebrow
        // končí na 24, nadpis začína na 56) a mobil `60042:5647` (21 → 37).
        subtitle && 'gap-4 sm:gap-5 md:gap-6 lg:gap-7 xl:gap-8',
        center && 'items-center text-center',
        className?.container,
      )}
    >
      {!!subtitle && (
        <span
          className={cn(
            'ea-heading__subtitle',
            'eyebrow inline-flex items-center gap-1 whitespace-nowrap',
            // `primary` (#1A5FD6) je jediná premenná farba v projekte, zvyšok
            // sa píše hexom. Na svetlom podklade je eyebrow v značkovej modrej,
            // na tmavom/modrom podklade biely — odmerané zo sekcie Kontakt
            // (`47:1792`), kde je celý eyebrow vrátane zátvoriek Text/On-Dark.
            theme === 'dark' ? 'text-[#FFFFFF]' : 'text-primary',
            className?.subtitle,
          )}
        >
          <span aria-hidden="true">[</span>
          {subtitle}
          <span aria-hidden="true">]</span>
        </span>
      )}

      {/* Nadpis a popis tvoria vo Figme jeden blok s vlastnou medzerou 16,
          nezávislou od medzery eyebrow → nadpis (32). */}
      <div
        className={cn(
          'flex w-full flex-col',
          description && 'gap-3 sm:gap-3.5 lg:gap-4',
          center && 'items-center',
        )}
      >
        {!!title && (
          <Tag
            dangerouslySetInnerHTML={{ __html: title }}
            className={cn(
              tag,
              'ea-heading__title',
              'relative z-[5]',
              // Nadpis na tmavom/modrom podklade je biely, nie tlmený —
              // primary-foreground je vyhradený pre sekundárny text.
              theme === 'dark' ? 'text-[#FFFFFF]' : 'text-[#212121]',
              center && 'text-center',
              className?.title,
            )}
            style={titleMaxWidth ? { maxWidth: titleMaxWidth } : undefined}
          />
        )}

        {!!description && (
          <p
            className={cn(
              'ea-heading__description',
              // Figma Body/L — 16/1,31 na mobile, 18/1,44 na desktope,
              // nulové prekladanie.
              'max-w-[688px] text-[16px] leading-[1.31] tracking-normal',
              'md:text-[17px] md:leading-[1.38] lg:text-[18px] lg:leading-[1.44]',
              // Na tmavom/modrom podklade biely (Kontakt `47:1794`),
              // na svetlom tlmený (Proces `51:2893`).
              theme === 'dark' ? 'text-[#FFFFFF]' : 'text-[#55617A]',
              center && 'text-center',
              className?.description,
            )}
          >
            {description}
          </p>
        )}
      </div>
    </div>
  );
}
